import Excel, { Worksheet } from 'exceljs';
import { find, get, isFinite, set, toNumber, map, some, findKey, values, flatMapDeep } from 'lodash';
import { PROJECT_TYPES, USER_ENGAGEMENT_ROLES, USER_ENGAGEMENT_SOURCES } from '@/constants';
import { UserDTO, UserEngagementDTO } from '@/models';

export enum FieldKey {
  id = 'id',
  contribution = 'contribution',
  key = 'key',
  note = 'note',
  role = 'role',
  user = 'user',
  individualDrops = 'individualDrops',
  userEngagementRole = 'userEngagementRole'
}

export type SheetRowData = {
  [key in FieldKey]?: string;
};

enum ErrorType {
  none,
  isNotNumber,
  isUndefined,
  cannotFindUser,
  isNotAcceptRole
}

interface VerifiedValue {
  value?: string | number | UserDTO;
  errorCode?: ErrorType;
  fieldName: string;
}

export interface Field {
  value?: string | number;
  type: Array<typeof String | typeof Number>;
  name: string;
  display: string;
}

interface TeamMemberParams {
  projectType: string;
  users: UserDTO[];
  file: File;
}

interface DataResult {
  importedData: UserEngagementDTO[];
  errors: string[];
}

export default class ImportTeamMemberService {
  private file: File;
  private workbook = new Excel.Workbook();
  private importedData = [] as UserEngagementDTO[];
  private fieldToColumnMapping: Field[] = [];
  private readonly DEFAULT_SHEET_INDEX = 0;
  private projectType: string = PROJECT_TYPES.INDIVIDUAL_TRACKING;
  private userList: UserDTO[] = [];
  private errors = [] as string[];
  private whiteListKey = ['note'];

  constructor(teamMemberParams: TeamMemberParams) {
    this.file = teamMemberParams.file;
    this.projectType = teamMemberParams.projectType;
    this.userList = teamMemberParams.users;
    this.fieldToColumnMapping = ImportTeamMemberService.initFieldToColumn(teamMemberParams.projectType);
  }

  updateFile(file: File): void {
    this.file = file;
  }

  static initFieldToColumn(type: string): Field[] {
    return ImportTeamMemberService.mapFieldToColumn(type, {
      id: 'A',
      contribution: 'B',
      key: 'C',
      note: 'D',
      role: 'C'
    });
  }

  static mapFieldToColumn(type: string, sheetRowData?: SheetRowData): Field[] {
    switch (type) {
      case PROJECT_TYPES.INDIVIDUAL_TRACKING:
        return INDIVIDUAL_TRACKING_FIELD.map(field => ({
          ...field,
          value: get(sheetRowData, field.name)
        })) as Field[];
      case PROJECT_TYPES.HOURLY_BASED:
        return HOURLY_BASED_FIELD.map(field => ({
          ...field,
          value: get(sheetRowData, field.name)
        }));
      case PROJECT_TYPES.FIXED:
        return FIXED_FIELD.map(field => ({
          ...field,
          value: get(sheetRowData, field.name)
        }));
      default:
        return HOURLY_BASED_FIELD.map(field => ({
          ...field,
          value: get(sheetRowData, field.name)
        }));
    }
  }

  get result(): DataResult {
    return {
      importedData: this.importedData,
      errors: this.errors
    };
  }
  updateFieldColumnMapping(newMapping: SheetRowData): void {
    this.fieldToColumnMapping = ImportTeamMemberService.mapFieldToColumn(this.projectType, newMapping);
  }

  async execute(): Promise<void> {
    await this.readFile();
  }

  get keyMaps(): string[] {
    return map(this.fieldToColumnMapping, field => field.name);
  }

  getFieldValue(key: string): Field {
    return find(this.fieldToColumnMapping, field => field.name === key) as Field;
  }

  getUserByKmsId(code: string | number): UserDTO | null {
    return find(this.userList, user => +user.code === +code) || null;
  }

  async getFileBuffer(file: File): Promise<Buffer> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsArrayBuffer(file);
      reader.onload = () => {
        resolve(reader.result as Buffer);
      };
      reader.onerror = reject;
    });
  }

  async readFile(): Promise<void> {
    this.errors = [];
    const buffer = await this.getFileBuffer(this.file);
    await this.workbook.xlsx.load(buffer);
    await this.loadDataFromSheet();
  }

  getSheetByIndex(index: number): Worksheet {
    return this.workbook.worksheets[index] || null;
  }

  async loadDataFromSheet(): Promise<void> {
    const worksheet = this.getSheetByIndex(this.DEFAULT_SHEET_INDEX);
    this.importedData = [];
    for (let pos = 1; pos < worksheet.actualRowCount; pos++) {
      const dataByRow = this.getDataByRow(worksheet, pos);
      if (this.errors.length > 0) {
        return;
      }
      this.importedData.push(dataByRow);
    }
  }

  getValueByKey(key: string, value: any, type: Array<typeof String | typeof Number>): VerifiedValue {
    if (!value && !this.whiteListKey.includes(key)) {
      return {
        errorCode: ErrorType.isUndefined,
        fieldName: key
      };
    }
    //#region check value is role
    if (key === FieldKey.role) {
      const acceptedRoles = {
        [USER_ENGAGEMENT_ROLES.PROJECT_MANAGER]: ['PM', 'PROJECT_MANAGER', 'PROJECT MANAGER'],
        [USER_ENGAGEMENT_ROLES.PROJECT_MEMBER]: ['MEMBER']
      };
      const isRoleAccepted = some(
        flatMapDeep(values(acceptedRoles)),
        role => role.toString().toLowerCase() === value.toString().toLowerCase()
      );
      if (isRoleAccepted) {
        const roleKey = findKey(acceptedRoles, roles =>
          some(roles, role => role.toString().toLowerCase() === value.toString().toLowerCase())
        );
        return {
          fieldName: FieldKey.userEngagementRole,
          value: roleKey
        };
      } else {
        return {
          errorCode: ErrorType.isNotAcceptRole,
          fieldName: key,
          value
        };
      }
    }
    //#endregion

    //#region check value is id
    if (key === FieldKey.id) {
      const user = this.getUserByKmsId(value || '');
      if (user) {
        return {
          value: user,
          fieldName: FieldKey.user
        };
      } else {
        return {
          errorCode: ErrorType.cannotFindUser,
          fieldName: FieldKey.user,
          value
        };
      }
    }
    //#endregion

    //#region check value by type
    if (type.includes(Number) && type.includes(String)) {
      return {
        value,
        fieldName: key
      };
    } else if (type.includes(Number)) {
      const numberValue = toNumber(value);
      const isValidNumber = isFinite(numberValue);
      if (isValidNumber) {
        return {
          value: numberValue,
          fieldName: key
        };
      } else {
        return {
          errorCode: ErrorType.isNotNumber,
          fieldName: key,
          value
        };
      }
    } else {
      return {
        value,
        fieldName: key
      };
    }
    //#endregion
  }

  getErrorMessage(errorType: ErrorType, value: any, cell: string): string {
    switch (errorType) {
      case ErrorType.isNotNumber:
        return `Value ${value} is not a number in cell ${cell}`;
      case ErrorType.isUndefined:
        return `Value in cell: ${cell} is empty`;
      case ErrorType.cannotFindUser:
        return `Cannot find User with ID: ${value} in cell: ${cell}`;
      case ErrorType.isNotAcceptRole:
        return `Role ${value} is not accepted in this project`;
      default:
        return '';
    }
  }

  getDataByRow(sheet: Worksheet, rowIndex: number): UserEngagementDTO {
    return this.keyMaps.reduce((preData: UserEngagementDTO, key) => {
      const fieldData = this.getFieldValue(key);
      const fieldValue = fieldData.value;
      const fieldType = fieldData.type;
      const cellNumber = rowIndex + 1;
      const cell = `${fieldValue}${cellNumber}`;
      const cellValue = sheet.getCell(cell).value;
      const { fieldName, value, errorCode } = this.getValueByKey(key, cellValue as string, fieldType);
      if (errorCode) {
        const errorMessage = this.getErrorMessage(errorCode, value, cell);
        this.errors.push(errorMessage);
      }
      if (preData.key && preData.contribution) {
        preData.individualDrops = +preData.key * +preData.contribution;
      }
      const returnValue = set({}, fieldName, value);
      return {
        ...returnValue,
        ...preData
      };
    }, { source: USER_ENGAGEMENT_SOURCES.IMPORTED_FROM_FILE } as UserEngagementDTO);
  }
}

const INDIVIDUAL_TRACKING_FIELD = [
  {
    name: 'id',
    type: [String, Number],
    display: 'ID'
  },
  {
    name: 'contribution',
    type: [Number],
    display: 'Contribution'
  },
  {
    name: 'key',
    type: [Number],
    display: 'Pollens'
  },
  {
    name: 'note',
    type: [String, Number],
    display: 'Note'
  }
];

const HOURLY_BASED_FIELD = [
  {
    name: 'id',
    type: [String, Number],
    display: 'ID'
  },
  {
    name: 'contribution',
    type: [Number],
    display: 'Contribution'
  },
  {
    name: 'role',
    type: [String, Number],
    display: 'Role'
  }
] as Field[];

const FIXED_FIELD = HOURLY_BASED_FIELD as Field[];
